import { CheckIcon, ClockIcon } from "@heroicons/react/24/outline";
import {
    isPhoneNumberE164,
    PhoneNumberE164,
    ReferenceContactId,
    RegionId,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import { Header } from "components/header";
import { MessageCompose } from "components/messages/message-compose";
import { trpc } from "components/rpc/trpc";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useState } from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { handleEvent } from "util/user-events";
import {
    AgendaAddContactPhone,
    AgendaCallAction,
    AgendaEmailAction,
    AgendaProvisionPhone,
    AgendaTextAction,
    AgendaTouchpointOverflowMenu,
    AgendaVisitAction,
} from "./agenda-actions";
import { resolveTouchpointActions } from "./marketing-plan-agenda";
import { describeCadence, describeChannels } from "./marketing-plan-format";

interface ContactPlanTouchpointRow {
    touchpointId: string;
    kinds: string[];
    intervalUnit: string;
    intervalCount: number;
    lastContactedAt?: string | null;
    nextDueAt?: string | null;
    state: "due" | "satisfied" | "snoozed";
    visitScheduled: boolean;
}

interface ReferenceContactMarketingPlanProps {
    contactId: ReferenceContactId;
}

export const ReferenceContactMarketingPlan: React.FC<
    ReferenceContactMarketingPlanProps
> = ({ contactId }) => {
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const phoneLines = usePhoneLines();
    const [isComposing, setIsComposing] = useState(false);

    const planQuery = useQuery({
        ...trpcClient.marketingPlaybook.contactPlan.queryOptions({
            regionId: regionId ?? ("" as RegionId),
            contactId,
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    const plan = planQuery.data;
    if (!plan) {
        return null;
    }

    const phone =
        plan.contactPhone && isPhoneNumberE164(plan.contactPhone)
            ? toPhoneNumberE164(plan.contactPhone)
            : null;

    const openCompose = () => {
        handleEvent("marketing-plan-send-email");
        setIsComposing(true);
    };

    return (
        <>
            <InputFormBlock>
                <Header iconName="25_ActiveTasks">Marketing Plan</Header>
                <div className="divide-y divide-gray-100">
                    {plan.touchpoints.map(touchpoint => (
                        <ContactPlanRow
                            key={touchpoint.touchpointId}
                            touchpoint={touchpoint}
                            contactId={contactId}
                            contactName={plan.contactName}
                            contactEmail={plan.contactEmail}
                            contactEmailOptedOut={plan.contactEmailOptedOut}
                            phone={phone}
                            hasPhoneLine={phoneLines.length > 0}
                            onSendEmail={openCompose}
                        />
                    ))}
                </div>
            </InputFormBlock>
            {isComposing ? (
                <MessageCompose
                    entityId={contactId}
                    entityType={AdvancedSearchEntityType.ReferenceContact}
                    onDismiss={() => setIsComposing(false)}
                />
            ) : null}
        </>
    );
};

interface ContactPlanRowProps {
    touchpoint: ContactPlanTouchpointRow;
    contactId: ReferenceContactId;
    contactName: string;
    contactEmail: string | null;
    contactEmailOptedOut: boolean;
    phone: PhoneNumberE164 | null;
    hasPhoneLine: boolean;
    onSendEmail: () => void;
}

const ContactPlanRow: React.FC<ContactPlanRowProps> = ({
    touchpoint,
    contactId,
    contactName,
    contactEmail,
    contactEmailOptedOut,
    phone,
    hasPhoneLine,
    onSendEmail,
}) => (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
            <div className="font-medium text-gray-700">
                {describeChannels(touchpoint.kinds)}{" "}
                {describeCadence(touchpoint.intervalCount, touchpoint.intervalUnit)}
            </div>
            <ContactPlanRowStatus touchpoint={touchpoint} />
        </div>
        {touchpoint.state === "due" ? (
            <ContactPlanRowActions
                touchpoint={touchpoint}
                contactId={contactId}
                contactName={contactName}
                contactEmail={contactEmail}
                contactEmailOptedOut={contactEmailOptedOut}
                phone={phone}
                hasPhoneLine={hasPhoneLine}
                onSendEmail={onSendEmail}
            />
        ) : null}
    </div>
);

const ContactPlanRowStatus: React.FC<{ touchpoint: ContactPlanTouchpointRow }> = ({
    touchpoint,
}) => {
    if (touchpoint.state === "snoozed") {
        return (
            <div className="flex items-center gap-1 text-gray-500">
                <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" />
                Snoozed
            </div>
        );
    }
    if (touchpoint.state === "satisfied") {
        return (
            <div className="flex items-center gap-1 text-green-700">
                <CheckIcon className="h-3.5 w-3.5 flex-shrink-0" />
                {satisfiedLabel(touchpoint)}
            </div>
        );
    }
    return <div className="text-gray-500">{dueLabel(touchpoint)}</div>;
};

const satisfiedLabel = (touchpoint: ContactPlanTouchpointRow): string => {
    if (touchpoint.visitScheduled) {
        return "Visit scheduled";
    }
    const done = touchpoint.lastContactedAt
        ? `Done ${new Date(touchpoint.lastContactedAt).toLocaleDateString()}`
        : "Done";
    return touchpoint.nextDueAt
        ? `${done} — next due ${new Date(touchpoint.nextDueAt).toLocaleDateString()}`
        : done;
};

const dueLabel = (touchpoint: ContactPlanTouchpointRow): string =>
    touchpoint.lastContactedAt
        ? `Due — last reached ${new Date(touchpoint.lastContactedAt).toLocaleDateString()}`
        : "Due — not yet reached";

const ContactPlanRowActions: React.FC<ContactPlanRowProps> = ({
    touchpoint,
    contactId,
    contactName,
    contactEmail,
    contactEmailOptedOut,
    phone,
    hasPhoneLine,
    onSendEmail,
}) => {
    const actions = resolveTouchpointActions({
        kinds: touchpoint.kinds,
        contactEmail,
        contactEmailOptedOut,
        hasContactPhone: !!phone,
        hasPhoneLine,
    });

    return (
        <div className="flex flex-wrap gap-2 sm:justify-end">
            {actions.email ? (
                <AgendaEmailAction
                    state={actions.email}
                    contactId={contactId}
                    onSendEmail={onSendEmail}
                />
            ) : null}
            {actions.addContactPhone ? (
                <AgendaAddContactPhone contactId={contactId} />
            ) : null}
            {actions.provisionPhoneLine ? <AgendaProvisionPhone /> : null}
            {actions.call && phone ? (
                <AgendaCallAction phone={phone} entityId={contactId} />
            ) : null}
            {actions.text && phone ? <AgendaTextAction phone={phone} /> : null}
            {actions.visit ? (
                <AgendaVisitAction contactId={contactId} contactName={contactName} />
            ) : null}
            <AgendaTouchpointOverflowMenu
                contactId={contactId}
                contactName={contactName}
                touchpointId={touchpoint.touchpointId}
                kinds={touchpoint.kinds}
            />
        </div>
    );
};
